import { useEffect } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/react';
import { IS_CLERK_SATELLITE } from '@/components/themed-clerk-provider.component';
import { rememberPostAuthRedirect } from '@/lib/post-auth-redirect';

/**
 * The fallback of the app's `<Show when='signed-in'>` gates: while the user
 * is signed out it sends them to log in (or register).
 *
 * It also mounts while the user is still signed in. Clerk's `<Show>` reads
 * `useAuth`, which counts a pending session as signed out, while `useUser`
 * here still has the user. A signed-in user is sent home only from an auth
 * route. Anywhere else they stay where they are, so the gate shows the page
 * again once Clerk reports the session, instead of dropping them on Home in
 * the middle of what they were doing.
 */
export const AuthRouter = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const pathname = useLocation({ select: (location) => location.pathname });
  const onAuthRoute = pathname.startsWith('/auth');

  // Light mode for the sign-in screens, never for a signed-in user the gate
  // falls back on for a moment: their own theme stays.
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      document.documentElement.classList.remove('dark');
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    // Signed in: from an auth route, on to the app; anywhere else, stay.
    if (isLoaded && isSignedIn) {
      // replace: a push leaves the auth page behind, so Back lands on it and
      // this sends them straight forward again, a loop Back can't escape.
      if (onAuthRoute) navigate({ to: '/', replace: true });
      return;
    }

    if (isLoaded && !isSignedIn) {
      // A shared link (a finder search's ?q=) opens once they've signed in.
      rememberPostAuthRedirect(
        `${window.location.pathname}${window.location.search}`
      );
      const hasAuthenticatedBefore = localStorage.getItem(
        'cleanlist_has_authenticated'
      );

      // The flag lives in this origin's storage, so on the Clu satellite
      // (app.clugtm.com) it is empty until a first sign-in there, and every
      // returning user redirected there was sent to sign up. Sign-in on the
      // primary links to sign-up for anyone new.
      if (hasAuthenticatedBefore || IS_CLERK_SATELLITE) {
        // Returning user - show login
        navigate({ to: '/auth/login', replace: true });
      } else {
        // New user - show sign up
        navigate({ to: '/auth/register', replace: true });
      }
    }
  }, [isLoaded, isSignedIn, navigate, onAuthRoute]);

  return (
    <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-white to-slate-50'>
      <div className='flex flex-col items-center gap-3'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-[#3E8BFF] border-t-transparent'></div>
        <p className='text-sm text-slate-600'>Loading...</p>
      </div>
    </div>
  );
};
